/// <amd-module name="Build/Queue-Panel/RepositoryResourcePanel" />
import * as React from "react";

import { RepositoryResourceData } from "Build/Client/Contracts/DataProvider";
import { BranchDropdown } from "Build/Common/Library/Legacy/Components/BranchDropdown";
import * as Resources from "Build/Queue-Panel/Resources";
import { ISourceProvider, RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";

import { ITfsPageService } from "Tfs/Platform/Page";

import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";

export interface IRepositoryResourcePanelProps {
    pageContext: IVssPageContext;
    resource: RepositoryResourceData;
    onUpdate(resource: RepositoryResourceData): void;
    onDismiss(): void;
}

interface IRepositoryResourcePanelState extends RepositoryResourceData {
    errorMessage?: string;
}

const branchRefPrefix = "refs/heads/";
const tagRefPrefix = "refs/tags/";

export class RepositoryResourcePanel extends React.Component<IRepositoryResourcePanelProps, IRepositoryResourcePanelState> {

    constructor(props: IRepositoryResourcePanelProps) {
        super(props);
        const state: IRepositoryResourcePanelState = { ...props.resource };
        this._initializeState(state);
        this.state = state;
    }

    public render(): JSX.Element {
        const description = format(Resources.RepositoryResourcePanelDescription, this.sourceProvider.getName());
        return (
            <Panel
                backButtonProps={{ onClick: this.props.onDismiss }}
                contentClassName="queue-panel-child no-padding"
                description={description}
                escDismiss={true}
                onDismiss={this.props.onDismiss}
                titleProps={{ text: this.props.resource.name }}
                footerButtonProps={[
                    {
                        text: Resources.CancelButtonText,
                        onClick: this.props.onDismiss
                    },
                    {
                        text: Resources.RepositoryResourceUpdateButtonText,
                        primary: true,
                        disabled: !this._isDirty(),
                        onClick: this._update
                    }]}>
                <div className="flex-column flex-grow padding-horizontal-20 rhythm-vertical-8">
                    {this.state.errorMessage &&
                        <MessageCard severity={MessageCardSeverity.Error}>{this.state.errorMessage}</MessageCard>
                    }
                    {this._getBranchControl()}
                </div>
            </Panel>
        );
    }

    private _initializeState(state: IRepositoryResourcePanelState) {
        // If this is the friendly name for an Azure Git repository, then we also need the project name to fetch it.
        if (this._isTfsGit() && !state.projectName && !state.projectId) {
            const parts = this.props.resource.name.split("/");
            if (parts.length === 2) {
                state.projectName = parts[0];
                state.name = parts[1];
            }
            else {
                const tfsPageService = this.props.pageContext.getService<ITfsPageService>("ITfsPageService");
                const tfsPageData = tfsPageService.getData();
                if (tfsPageData && tfsPageData.project) {
                    state.projectName = tfsPageData.project.name;
                    state.projectId = tfsPageData.project.id;
                }
            }
        }
    }

    private _isTfsGit() {
        return equals(this.props.resource.type, RepositoryTypes.TfsGit, true);
    }

    private _getBranchControl() {
        const repository = this.state;

        const sourceSelectorDescription = this._isTfsGit() ? Resources.TfsGitSourceSelectorDescription : Resources.GitSourceSelectorDescription;

        return (
            <BranchDropdown
                className="flex-column label margin-bottom-16 flex-grow"
                ariaLabel={Resources.RepositoryResourceSourceLabel}
                label={Resources.RepositoryResourceSourceLabel}
                autoFocus={true}
                repositoryType={repository.type}
                repositoryId={this.state.name}
                projectId={repository.projectId || repository.projectName}
                defaultBranch={this._getFriendlyRefName(this.props.resource.refName)}
                description={sourceSelectorDescription}
                connectionId={repository.connectionId}
                currentCommit={repository.version}
                onBranchSelected={this._branchSelected}
                onBranchSelectedForCommit={this._branchSelectedForCommit}
                onError={this._onFetchRepoError}
                onVersionSelected={this._versionSelected}
                onTagSelected={this._tagSelected}
                viewCommits={true}
                viewTags={true}
                pageContext={this.props.pageContext}
                isDrodownFullWidth={true}
                showBranchPickerOnCommitSelected={true}
            />
        );
    }

    private _branchSelected = (branch: string) => {
        const refName = branch.startsWith(branchRefPrefix) ? branch : branchRefPrefix + branch;

        // override version if we are selecting a branch
        this.setState({ refName, version: "" });
    }

    private _branchSelectedForCommit = (branch: string) => {
        const refName = branch.startsWith(branchRefPrefix) ? branch : branchRefPrefix + branch;

        // leave version if we are selecting a branch for a commit
        this.setState({ refName });
    }

    private _tagSelected = (tag: string) => {
        const refName = tag.startsWith(tagRefPrefix) ? tag : tagRefPrefix + tag;
        this.setState({ refName, version: "" });
    }

    private _versionSelected = (commitSha: string) => {
        this.setState({ version: commitSha });
    }

    private _onFetchRepoError = (errorMessage: string) => {
        this.setState({ errorMessage });
    }

    private _getFriendlyRefName(refName: string) {
        refName = refName || "";
        if (refName.startsWith(branchRefPrefix)) {
            return refName.substr(branchRefPrefix.length);
        }
        else if (refName.startsWith(tagRefPrefix)) {
            return refName.substr(tagRefPrefix.length);
        }
        return refName;
    }

    private _update = () => {
        const resource = { ...this.state };
        this.props.onUpdate(resource);
        this.props.onDismiss();
    }

    private _isDirty(): boolean {
        return this.state.refName !== this.props.resource.refName
            || this.state.version !== this.props.resource.version
    }

    private get sourceProvider(): ISourceProvider {
        const sourceProviderService = this.props.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        return sourceProviderService.getSourceProvider(this.state.type);
    }
}
